import React, { useEffect, useState } from 'react'
import { IWidgetProps } from './utils'
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Grid,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material'
import WidgetDialog from '../WidgetDialog/WidgetDialog'
import Graph from './Visualizations/Graph'
import { POST_ENDPOINT } from '../../api-request'
import moment from 'moment'
import './styles.css'
import { capitalize, formatNumber } from '../../utils/utils'
import BarChart from './Visualizations/BarChart'

/**
 * Generic widget component to display analytics/metrics
 * @param props
 * @returns widget
 */
const Widget = (props: IWidgetProps) => {
  const { id, size, variant, heading, endpoint, params, frequency, stages } = props

  // Styling
  const theme = useTheme()
  const [mdSize, setMdSize] = useState<number>(3)

  // Widget states
  const [loading, setLoading] = useState(false)
  const [metric, setMetric] = useState<any>()
  const [units, setUnits] = useState<string>('')
  const [seriesData, setSeriesData] = useState<any>([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [rawData, setRawData] = useState<any>(null)
  const [filterable, setFilterable] = useState<boolean>(true)

  /**
   * Formats size of the widget within a grid
   */
  const formatSize = () => {
    switch (size) {
      case 'xs':
        setMdSize(3)
        break
      case 'sm':
        setMdSize(4)
        break
      case 'md':
        setMdSize(6)
        break
      case 'lg':
        setMdSize(9)
        break
      case 'xl':
        setMdSize(12)
        break
      default:
        break
    }
  }

  /**
   * Format data to be shown in visualization for widget
   * @param data
   */
  const formatData = (data: any) => {
    switch (frequency) {
      case 'daily':
        formatDailyData(data)
        break
      case 'hourly':
        formatHourlyData(data)
        break
      case 'multi':
        formatMultiData(data)
        break
      default:
        break
    }
  }

  /**
   * Formats daily data
   * @param data
   */
  const formatDailyData = (data: any) => {
    let series = new Array()
    for (let i = 0; i < data.length; i++) {
      let day = moment.utc(Object.keys(data[i])[0]).format('YYYY-MM-DD')
      let value = Object.values(data[i])[0]
      series.push([day, value])
      if (i == data.length - 1) {
        if (id == 'duration') {
          setMetric(value)
          setUnits('ms')
        } else {
          setMetric(value)
        }
      }
    }
    setSeriesData(series)
    setLoading(false)
  }

  /**
   * Formats hourly data
   * @param data
   */
  const formatHourlyData = (data: any) => {
    let series = new Array()
    for (let i = 0; i < data.length; i++) {
      let time = moment.utc(Object.keys(data[i])[0]).local().format()
      let value = Object.values(data[i])[0]
      series.push([time, value])
      if (i == data.length - 1) {
        setMetric(value)
      }
    }
    setSeriesData(series)
    setLoading(false)
  }

  /**
   * Formats multiline data
   * @param data
   */
  const formatMultiData = (data: any, stagesToShow: string[] = stages) => {
    let devSeries = new Array()
    let testSeries = new Array()
    let prodSeries = new Array()

    for (let i = 0; i < data.length; i++) {
      let time = moment.utc(Object.keys(data[i])[0]).local().format()
      let value: any = Object.values(data[i])[0]

      if (id == 'lambda-duration') {
        devSeries.push([time, value.dev / 1000])
        testSeries.push([time, value.test / 1000])
        prodSeries.push([time, value.prod / 1000])
      } else {
        devSeries.push([time, value.dev])
        testSeries.push([time, value.test])
        prodSeries.push([time, value.prod])
      }

      if (i == data.length - 1) {
        let prod = value.prod
        let dev = value.dev
        let test = value.test

        // Calculate total and average based on enabled stages only
        let enabledValues = []
        if (stagesToShow.includes('dev')) enabledValues.push(dev)
        if (stagesToShow.includes('test')) enabledValues.push(test)
        if (stagesToShow.includes('prod')) enabledValues.push(prod)

        let total = enabledValues.reduce((sum, val) => sum + val, 0)
        let avg = enabledValues.length > 0 ? total / enabledValues.length : 0

        if (id == 'elastic-cpu') {
          setMetric(avg.toFixed(1))
          setUnits('%')
          setFilterable(false)
        }
        if (id == 'cost') {
          setMetric(total.toFixed(2))
          setUnits('$')
          setFilterable(false)
        }
        if (id == 'lambda-duration') {
          setMetric((prod / 1000).toFixed(2))
          setUnits('s')
          setFilterable(false)
        }
        if (id == 'lambda-invocations') {
          setMetric(prod)
          setFilterable(false)
        }
        if (id == 'sms') {
          setMetric(total)
          setFilterable(false)
        }
        if (id == 'iops') {
          setMetric(prod)
          setFilterable(false)
        }
      }
    }

    setSeriesData({
      dev: stagesToShow.includes('dev') ? devSeries : [],
      test: stagesToShow.includes('test') ? testSeries : [],
      prod: stagesToShow.includes('prod') ? prodSeries : [],
    })

    setLoading(false)
  }

  /**
   * Handles opening the dialog
   */
  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  /**
   * Handles closing the dialog
   */
  const onClose = () => {
    setDialogOpen(false)
  }

  /**
   * Gets intial data for widget visualization
   * @returns
   */
  const getData = async () => {
    setLoading(true)
    const response = await POST_ENDPOINT(endpoint, params)
    return response
  }

  // Fetch data only once on mount
  useEffect(() => {
    formatSize()

    getData().then((res: any) => {
      let dataToFormat
      if (id == 'duration') {
        let meanData = new Array()

        // only use mean data for the duration widget
        res.docs.forEach((doc) => {
          let newDoc = {}
          newDoc[Object.keys(doc)[0]] = doc[Object.keys(doc)[0]].mean
          meanData.push(newDoc)
        })

        dataToFormat = meanData
      } else {
        dataToFormat = res.docs
      }

      // Store raw data for client-side filtering
      setRawData(dataToFormat)
      formatData(dataToFormat)
    })
  }, [])

  // Re-filter data when stages change (no API call)
  useEffect(() => {
    if (rawData && frequency === 'multi') {
      formatMultiData(rawData, stages)
    }
  }, [stages])

  return (
    <Grid item xs={12} sm={6} md={mdSize}>
      <Card
        variant="elevation"
        elevation={theme.palette.mode === 'dark' ? 0 : 1}
        sx={{
          background: theme.palette.mode === 'dark' ? 'rgba(23, 23, 23, 0.7)' : theme.palette.background.paper,
          borderRadius: '12px',
          backdropFilter: theme.palette.mode === 'dark' ? 'blur(20px)' : 'none',
          border: theme.palette.mode === 'dark' ? '1px solid rgba(64, 64, 64, 0.3)' : '1px solid rgba(0, 0, 0, 0.12)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(64, 64, 64, 0.2)'
              : '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 12px 48px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(115, 115, 115, 0.4)'
                : '0 4px 16px rgba(0, 0, 0, 0.15)',
            border:
              theme.palette.mode === 'dark' ? '1px solid rgba(115, 115, 115, 0.5)' : '1px solid rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <CardActionArea onClick={handleOpenDialog}>
          <CardHeader
            sx={{
              pb: 1,
            }}
            title={
              <Grid container justifyContent="space-between" alignItems={'center'} spacing={0}>
                <Grid item>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {heading ? heading : '...'}
                  </Typography>
                </Grid>
                <Grid item>
                  {loading ? (
                    <Skeleton
                      variant="text"
                      animation="wave"
                      width={60}
                      height={28}
                      sx={{
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                        borderRadius: '4px',
                      }}
                    />
                  ) : (
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        background:
                          theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #e5e5e5 0%, #a3a3a3 100%)'
                            : 'linear-gradient(135deg, #525252 0%, #262626 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {metric || metric == 0
                        ? id == 'cost'
                          ? units + formatNumber(metric)
                          : formatNumber(metric) + units
                        : '-'}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            }
          />
          <CardContent
            sx={{ height: { xs: '250px', sm: '30vh', md: '35vh' }, margin: 0, padding: 0, position: 'relative' }}
          >
            {loading && (
              <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Chart area skeleton */}
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    flex: 1,
                    borderRadius: '8px',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.06)',
                    '&::after': {
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.04), transparent)'
                        : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                    },
                  }}
                />
                {/* X-axis labels skeleton */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
                  {[...Array(5)].map((_, i) => (
                    <Skeleton
                      key={i}
                      variant="text"
                      animation="wave"
                      width={40}
                      height={16}
                      sx={{
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.06)',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {!loading && variant == 'graph' && (
              <Graph name={capitalize(id)} metric={metric} seriesData={seriesData} frequency={frequency} size={size} />
            )}
            {!loading && variant == 'chart' && (
              <BarChart
                name={capitalize(id)}
                metric={metric}
                seriesData={seriesData}
                frequency={frequency}
                size={size}
              />
            )}
            {!loading && variant == 'number' && <></>}
          </CardContent>
        </CardActionArea>
      </Card>
      <WidgetDialog {...props} open={dialogOpen} onClose={onClose} filterable={filterable} />
    </Grid>
  )
}

export default Widget
